import { useState, useEffect, useCallback, useMemo } from 'react';
import { filterData, sortData, paginateData } from '@/utils/dataUtils';
import { UseDataManagerOptions, UseDataManagerReturn } from '@/models/services';
import { FilterOptions } from '@/models/config';
import axios from 'axios';

/**
 * Custom hook for managing data with client-side filtering, sorting, and pagination
 */
export function useDataManager<T>(
  fetchData: () => Promise<T[]>,
  options: UseDataManagerOptions<T>
): UseDataManagerReturn<T> {
  const {
    searchFields,
    defaultPage = 1,
    defaultLimit = 50,
    defaultSortBy,
    defaultSortOrder = 'asc'
  } = options;

  // State
  const [allData, setAllData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterOptions>({
    page: defaultPage,
    limit: defaultLimit,
    sortBy: defaultSortBy as string,
    sortOrder: defaultSortOrder
  });

  const normalizeData = useCallback((payload: any): T[] => {
    if (Array.isArray(payload)) {
      return payload as T[];
    }

    if (payload && typeof payload === 'object') {
      const candidateKeys = ['data', 'items', 'customers', 'products', 'results', 'rows'];
      for (const key of candidateKeys) {
        const value = (payload as Record<string, unknown>)[key];
        if (Array.isArray(value)) {
          return value as T[];
        }
      }
    }

    return [];
  }, []);

  // Fetch data function with cancellation handling
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchData();
      setAllData(normalizeData(data));
    } catch (error: any) {
      // Don't show errors for cancelled requests
      if (axios.isCancel(error) || error.message === 'Request cancelled' || error.message === 'Duplicate request cancelled') {
        return;
      }

      const message = error?.message || 'Failed to load data';
      console.error('Error loading data:', error);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [fetchData]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Search+sort filtered data (unpaginated) — used by ManagementPage for status sub-filtering + manual pagination
  const filteredUnpaginated = useMemo(() => {
    let data = [...allData];
    if (filters.search) {
      data = filterData(data, filters.search, searchFields as (keyof T)[]);
    }
    if (filters.sortBy) {
      data = sortData(data, filters.sortBy as keyof T, filters.sortOrder);
    }
    return data;
  }, [allData, filters.search, filters.sortBy, filters.sortOrder, searchFields]);

  // Paginated result
  const processedData = useMemo(() => {
    return paginateData(filteredUnpaginated, filters.page, filters.limit);
  }, [filteredUnpaginated, filters.page, filters.limit]);

  // Update specific filter
  const updateFilter = useCallback((key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      // Reset to first page when filters change
      ...(key !== 'page' && { page: 1 })
    }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({
      page: defaultPage,
      limit: defaultLimit,
      sortBy: defaultSortBy as string,
      sortOrder: defaultSortOrder
    });
  }, [defaultPage, defaultLimit, defaultSortBy, defaultSortOrder]);

  // Refresh data
  const refreshData = useCallback(() => {
    loadData();
  }, [loadData]);

  // Export data
  const exportData = useCallback((filename?: string) => {
    // This would be implemented based on the specific data type
  }, [processedData.data]);

  return {
    // Data
    allData,
    filteredData: filteredUnpaginated,  // search+sort filtered, NOT paginated
    paginatedData: processedData,
    
    // Filter state
    filters,
    setFilters,
    updateFilter,
    clearFilters,
    
    // Loading state
    loading,
    setLoading,
    error,
    
    // Actions
    refreshData,
    exportData
  };
}

/**
 * Specialized hook for products
 */
export function useProductManager() {
  const fetchProducts = useCallback(async () => {
    const { ProductService } = await import('@/services/productService');
    const response = await ProductService.getProducts();
    return response.success ? response.data : [];
  }, []);

  return useDataManager(fetchProducts, {
    searchFields: ['item_code', 'item_name'],
    defaultSortBy: 'item_code',
    defaultSortOrder: 'asc'
  });
}

/**
 * Specialized hook for customers
 */
export function useCustomerManager() {
  const fetchCustomers = useCallback(async () => {
    const { CustomerService } = await import('@/services/customerService');
    const response = await CustomerService.getCustomers();
    return response.success ? response.data : [];
  }, []);

  return useDataManager(fetchCustomers, {
    searchFields: ['name'],
    defaultSortBy: 'name',
    defaultSortOrder: 'asc'
  });
}

/**
 * Specialized hook for invoices
 */
export function useInvoiceManager() {
  const fetchInvoices = useCallback(async () => {
    const { BillService } = await import('@/services/invoiceService');
const response = await BillService.getBills();
    return response.success ? response.data : [];
  }, []);

  return useDataManager(fetchInvoices, {
    searchFields: ['invoice_number', 'status'],
    defaultSortBy: 'invoice_number',
    defaultSortOrder: 'desc'
  });
}

/**
 * Specialized hook for users
 */
export function useUserManager() {
  const fetchUsers = useCallback(async () => {
    const { UserService } = await import('@/services/userService');
    const response = await UserService.getUsers();
    return response.success ? response.data : [];
  }, []);

  return useDataManager(fetchUsers, {
    searchFields: ['username', 'email', 'role'],
    defaultSortBy: 'username',
    defaultSortOrder: 'asc'
  });
} 